import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, 
  Users, 
  FileText, 
  Search, 
  DollarSign,
  TrendingUp,
  Loader2
} from "lucide-react";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/auth/AuthProvider";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface SaleDetail {
  id: string;
  type: 'mentorship' | 'digital_content';
  date: string;
  buyer_name: string;
  buyer_email: string;
  title: string;
  gross_amount_cents: number;
  commission_rate: number;
  commission_amount_cents: number;
  net_amount_cents: number;
  status: string;
  payment_method?: string;
}

interface SessionData {
  id: string;
  created_at: string;
  status: string;
  payment_released: boolean;
  payment_released_at?: string;
  player_profile_id: string;
  mentorship_offerings?: {
    title: string;
    price_cents: number;
  };
  payments?: Array<{
    id: string;
    amount_cents: number;
    status: string;
    created_at: string;
  }>;
  profiles?: {
    id: string;
    email: string;
    nickname: string;
  };
}

const DetalhesVendas = () => {
  console.log('🔍 [DetalhesVendas] Componente iniciado');
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sales, setSales] = useState<SaleDetail[]>([]);
  const [filteredSales, setFilteredSales] = useState<SaleDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("date_desc");
  const [currentCommissionRate, setCurrentCommissionRate] = useState<number>(20);

  console.log('👤 [DetalhesVendas] User:', user?.email);
  console.log('📊 [DetalhesVendas] Loading:', loading);
  console.log('🛒 [DetalhesVendas] Sales count:', sales.length);

  const formatCurrency = (cents: number) => {
    return (cents / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const formatPercentage = (rate: number) => {
    return `${rate.toFixed(0)}%`;
  };

  const fetchSalesData = async () => {
    console.log('📥 [DetalhesVendas] fetchSalesData iniciado');
    
    if (!user) {
      console.log('❌ [DetalhesVendas] Usuário não encontrado');
      return;
    }

    try {
      console.log('⏳ [DetalhesVendas] Definindo loading como true');
      setLoading(true);

      // Buscar informações do mentor
      const { data: mentorData, error: mentorError } = await supabase
        .from('mentor_info')
        .select('id, commission_rate')
        .eq('profile_id', user.id)
        .single();

      if (mentorError || !mentorData) {
        toast.error('Erro ao carregar dados do mentor');
        return;
      }

      const mentorId = mentorData.id;
      // Detectar formato da taxa de comissão atual do mentor
      const rawCommissionRate = mentorData.commission_rate || 0.2; // Default 20%
      
      // Se for um valor menor que 1, assumir que já está em decimal (0.2 = 20%)
      // Se for maior que 1, assumir que está em porcentagem (20 = 20%)
      const commissionRateAsPercentage = rawCommissionRate < 1 
        ? rawCommissionRate * 100  // 0.2 → 20
        : rawCommissionRate;       // 20 → 20
      
      // Salvar a taxa atual para exibir na interface
      setCurrentCommissionRate(commissionRateAsPercentage);
      
      const defaultCommissionRate = rawCommissionRate < 1 
        ? rawCommissionRate        // 0.2 → 0.2
        : rawCommissionRate / 100; // 20 → 0.2
      
      console.log(`💰 [DetalhesVendas] Taxa de comissão ATUAL: ${commissionRateAsPercentage}% (${defaultCommissionRate} decimal)`);

      const salesList: SaleDetail[] = [];

      // 1. BUSCAR VENDAS DE MENTORIAS (TODAS AS SESSÕES APROVADAS)
      

      
      // Debug: verificar todos os status e dados das sessões para este mentor
      const { data: allSessions, error: statusError } = await supabase
        .from('sessions')
        .select('id, status, payment_released, created_at')
        .eq('mentor_id', mentorId);
      
      if (allSessions) {
        const statusCounts = allSessions.reduce((acc, session) => {
          acc[session.status] = (acc[session.status] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);
        console.log('🔍 [DEBUG] Status disponíveis para este mentor:', statusCounts);
        console.log('🔍 [DEBUG] Todas as sessões do mentor:', allSessions.map(s => ({
          id: s.id,
          status: s.status,
          payment_released: s.payment_released,
          created_at: s.created_at
        })));
      }
      
      // Buscar TODAS as sessões concluídas (com ou sem registros na tabela payments)
      const { data: mentorshipSales, error: mentorshipError } = await supabase
        .from('sessions')
        .select(`
          id,
          created_at,
          status,
          payment_released,
          payment_released_at,
          player_profile_id,
          mentorship_offerings:offering_id (
            title,
            price_cents
          ),
          payments (
            id,
            amount_cents,
            status,
            created_at
          ),
          profiles:player_profile_id (
            id,
            email,
            nickname
          )
        `)
        .eq('mentor_id', mentorId)
        .in('status', ['concluido', 'completed', 'finalizado', 'approved', 'pago', 'liberado', 'confirmado']);

      console.log('📊 [DetalhesVendas] Sessões concluídas encontradas:', mentorshipSales?.length || 0);
      console.log('🔍 [DetalhesVendas] Mentor ID usado na query:', mentorId);
      
      if (mentorshipError) {
        console.error('❌ [DetalhesVendas] Erro na query de mentorias:', mentorshipError);
      }
      
      if (mentorshipSales) {
        console.log('📋 [DetalhesVendas] Detalhes das sessões encontradas:');
        mentorshipSales.forEach((s, index) => {
          console.log(`  ${index + 1}. Sessão ${s.id}:`, {
            status: s.status,
            payment_released: s.payment_released,
            offering_price: s.mentorship_offerings?.price_cents,
            payments_count: s.payments?.length || 0,
            payments_details: s.payments?.map(p => ({ status: p.status, amount: p.amount_cents })) || [],
            buyer: s.profiles?.nickname || s.profiles?.email
          });
        });
      }

      if (!mentorshipError && mentorshipSales) {
        mentorshipSales.forEach((session, index) => {
          console.log(`🔄 [DetalhesVendas] Processando mentoria ${index + 1}:`, {
            id: session.id,
            status: session.status,
            offering: session.mentorship_offerings,
            payments: session.payments,
            payment_released: session.payment_released,
            player: session.profiles
          });
          
          // Título da mentoria
          let mentorshipTitle = 'Mentoria';
          if (session.mentorship_offerings) {
            mentorshipTitle = session.mentorship_offerings.title || 'Mentoria';
          }
          
          // Determinar valor pago (priorizar pagamento, depois offering)
          let grossAmount = 0;
          let paymentDate = session.created_at;
          
          if (session.payments && session.payments.length > 0) {
            // Priorizar pagamento completed
            const completedPayment = session.payments.find(p => p.status === 'completed');
            if (completedPayment) {
              grossAmount = completedPayment.amount_cents;
              paymentDate = completedPayment.created_at;
              console.log(`💰 [DetalhesVendas] Mentoria ${session.id} - valor do pagamento: ${grossAmount}`);
            } else {
              // Usar qualquer pagamento disponível
              const anyPayment = session.payments[0];
              if (anyPayment && anyPayment.amount_cents > 0) {
                grossAmount = anyPayment.amount_cents;
                paymentDate = anyPayment.created_at;
                console.log(`⚠️ [DetalhesVendas] Mentoria ${session.id} - usando pagamento ${anyPayment.status}: ${grossAmount}`);
              }
            }
          }
          
          // Se não tem pagamento, usar valor do offering
          if (grossAmount === 0 && session.mentorship_offerings?.price_cents) {
            grossAmount = session.mentorship_offerings.price_cents;
            console.log(`🎯 [DetalhesVendas] Mentoria ${session.id} - valor do offering: ${grossAmount}`);
          }
          
          if (grossAmount > 0) {
            // Por enquanto, usar a taxa atual do mentor (histórico será implementado futuramente)
            // TODO: Implementar campo commission_rate na tabela payments via migração
            const historicalCommissionRate = defaultCommissionRate; // Usar taxa atual
            const historicalCommissionRateAsPercentage = commissionRateAsPercentage;
            
            console.log(`💰 [DetalhesVendas] Mentoria ${session.id} - usando taxa atual: ${historicalCommissionRateAsPercentage}%`);
            
            const commissionAmount = Math.round(grossAmount * historicalCommissionRate);
            const netAmount = grossAmount - commissionAmount;
            
            console.log(`💰 [DetalhesVendas] Cálculo financeiro para mentoria ${session.id}:`, {
              valorBruto: `R$ ${(grossAmount/100).toFixed(2)}`,
              taxaComissao: `${(historicalCommissionRate * 100).toFixed(0)}%`,
              valorComissao: `R$ ${(commissionAmount/100).toFixed(2)}`,
              valorLiquido: `R$ ${(netAmount/100).toFixed(2)}`
            });

            // Determinar status baseado na liberação do pagamento
            let sessionStatus = 'completed';
            
            // Se a sessão tem status "confirmado", sempre considerar como "completed"
            if (session.status === 'confirmado') {
              sessionStatus = 'completed';
              console.log(`✅ [DetalhesVendas] Mentoria ${session.id} - status "confirmado" → "completed"`);
            } else if (session.payment_released) {
              sessionStatus = 'released';
              console.log(`💰 [DetalhesVendas] Mentoria ${session.id} - payment_released=true → "released"`);
            } else {
              sessionStatus = 'pending_release';
              console.log(`⏳ [DetalhesVendas] Mentoria ${session.id} - payment_released=false → "pending_release"`);
            }

            salesList.push({
              id: `mentorship_${session.id}`,
              type: 'mentorship',
              date: session.payment_released_at || paymentDate,
              buyer_name: session.profiles?.nickname || 'Nome não informado',
              buyer_email: session.profiles?.email || '',
              title: mentorshipTitle,
              gross_amount_cents: grossAmount,
              commission_rate: historicalCommissionRateAsPercentage, // Usar taxa histórica como porcentagem inteira
              commission_amount_cents: commissionAmount,
              net_amount_cents: netAmount,
              status: sessionStatus,
              payment_method: 'stripe'
            });
            
            console.log(`✅ [DetalhesVendas] Mentoria adicionada: ${mentorshipTitle} - R$ ${(grossAmount/100).toFixed(2)} (pago em ${paymentDate})`);
          } else {
            console.log(`❌ [DetalhesVendas] Mentoria ${session.id} ignorada - sem pagamento válido. Detalhes:`, {
              offering_price: session.mentorship_offerings?.price_cents,
              payments_count: session.payments?.length || 0,
              payments_details: session.payments || [],
              status: session.status,
              payment_released: session.payment_released
            });
          }
        });
      }

      // 2. BUSCAR VENDAS DE CONTEÚDOS DIGITAIS
      const { data: digitalContentSales, error: digitalContentError } = await supabase
        .from('digital_content_purchases')
        .select(`
          id,
          purchase_price_cents,
          created_at,
          payment_status,
          payment_method,
          commission_rate,
          mentor_earnings_cents,
          platform_earnings_cents,
          digital_content:digital_content_id!inner (
            id,
            title,
            content_type,
            mentor_id
          ),
          profiles:buyer_id (
            id,
            email,
            nickname
          )
        `)
        .eq('digital_content.mentor_id', mentorId)
        .eq('payment_status', 'completed');

      if (!digitalContentError && digitalContentSales) {
        digitalContentSales.forEach(purchase => {
          const grossAmount = purchase.purchase_price_cents;
          const commissionRate = purchase.commission_rate || defaultCommissionRate;
          const commissionAmount = purchase.platform_earnings_cents || Math.round(grossAmount * commissionRate);
          const netAmount = purchase.mentor_earnings_cents || (grossAmount - commissionAmount);

          // Converter taxa de comissão para porcentagem inteira para exibição
          const commissionRateForDisplay = typeof commissionRate === 'number' && commissionRate < 1 
            ? commissionRate * 100  // Se for decimal (0.2), converter para porcentagem (20)
            : commissionRate;       // Se já for porcentagem (20), manter

          salesList.push({
            id: `digital_content_${purchase.id}`,
            type: 'digital_content',
            date: purchase.created_at,
            buyer_name: purchase.profiles?.nickname || 'Nome não informado',
            buyer_email: purchase.profiles?.email || '',
            title: purchase.digital_content?.title || 'Conteúdo Digital',
            gross_amount_cents: grossAmount,
            commission_rate: commissionRateForDisplay, // Usar taxa como porcentagem inteira
            commission_amount_cents: commissionAmount,
            net_amount_cents: netAmount,
            status: 'completed',
            payment_method: purchase.payment_method
          });
        });
      }

      // Ordenar por data (mais recente primeiro)
      salesList.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      console.log('✅ [DetalhesVendas] Dados carregados:', salesList.length, 'vendas');
      console.log('📊 [DetalhesVendas] Breakdown:');
      console.log('   - Mentorias:', salesList.filter(s => s.type === 'mentorship').length);
      console.log('   - Conteúdos Digitais:', salesList.filter(s => s.type === 'digital_content').length);
      
      setSales(salesList);
      setFilteredSales(salesList);

    } catch (error) {
      console.error('❌ [DetalhesVendas] Erro ao buscar vendas:', error);
      toast.error('Erro ao carregar histórico de vendas');
    } finally {
      console.log('🏁 [DetalhesVendas] Definindo loading como false');
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...sales];

    // Filtro por tipo
    if (typeFilter !== "all") {
      filtered = filtered.filter(sale => sale.type === typeFilter);
    }

    // Filtro por busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(sale => 
        sale.buyer_name.toLowerCase().includes(term) ||
        sale.title.toLowerCase().includes(term) ||
        sale.buyer_email.toLowerCase().includes(term)
      );
    }

    // Ordenação
    switch (sortBy) {
      case "date_desc":
        filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
      case "date_asc":
        filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        break;
      case "amount_desc":
        filtered.sort((a, b) => b.gross_amount_cents - a.gross_amount_cents);
        break;
      case "amount_asc":
        filtered.sort((a, b) => a.gross_amount_cents - b.gross_amount_cents);
        break;
      case "buyer_name":
        filtered.sort((a, b) => a.buyer_name.localeCompare(b.buyer_name));
        break;
    }

    setFilteredSales(filtered);
  };

  useEffect(() => {
    console.log('🔄 [DetalhesVendas] useEffect fetchSalesData disparado');
    fetchSalesData();
  }, [user]);

  useEffect(() => {
    console.log('🔄 [DetalhesVendas] useEffect applyFilters disparado');
    applyFilters();
  }, [searchTerm, typeFilter, sortBy, sales]);

  const totalGross = filteredSales.reduce((sum, sale) => sum + sale.gross_amount_cents, 0);
  const totalCommission = filteredSales.reduce((sum, sale) => sum + sale.commission_amount_cents, 0);
  const totalNet = filteredSales.reduce((sum, sale) => sum + sale.net_amount_cents, 0);

  if (loading) {
    console.log('⏳ [DetalhesVendas] Renderizando estado de loading...');
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-12 w-12 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  console.log('🎨 [DetalhesVendas] Renderizando componente principal...');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          onClick={() => navigate('/painel-mentor/financas')}
          variant="outline"
          className="border-[#2A3749] bg-[#1E293B] text-white hover:bg-[#2A3749]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <h1 className="text-3xl font-bold text-white">Detalhes das Vendas</h1>
      </div>

      {/* Resumo dos Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-400" />
              <div>
                <p className="text-sm text-gray-400">Total Bruto</p>
                <p className="text-lg font-bold text-green-400">{formatCurrency(totalGross)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-400" />
              <div>
                <p className="text-sm text-gray-400">Total Comissão</p>
                <p className="text-lg font-bold text-orange-400">{formatCurrency(totalCommission)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-400" />
              <div>
                <p className="text-sm text-gray-400">Total Líquido</p>
                <p className="text-lg font-bold text-blue-400">{formatCurrency(totalNet)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Taxa de Comissão Atual */}
      <Card className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-blue-500/30">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <TrendingUp className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-blue-200">Sua taxa de comissão atual</p>
              <p className="text-lg font-bold text-blue-400">{currentCommissionRate.toFixed(0)}%</p>
            </div>
            <div className="ml-auto text-xs text-blue-300 max-w-md">
              <p>Esta é sua taxa atual configurada pelo administrador. As vendas anteriores mantêm a taxa da época da venda.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card className="bg-[#1E293B] border-[#2A3749]">
        <CardHeader>
          <CardTitle className="text-white">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Busca */}
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por comprador ou produto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-[#0F1419] border-[#2A3749] text-white"
                />
              </div>
            </div>

            {/* Filtro por tipo */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="bg-[#0F1419] border-[#2A3749] text-white">
                <SelectValue placeholder="Tipo de venda" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem value="mentorship">Mentorias</SelectItem>
                <SelectItem value="digital_content">Conteúdos Digitais</SelectItem>
              </SelectContent>
            </Select>

            {/* Ordenação */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="bg-[#0F1419] border-[#2A3749] text-white">
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date_desc">Data (mais recente)</SelectItem>
                <SelectItem value="date_asc">Data (mais antiga)</SelectItem>
                <SelectItem value="amount_desc">Valor (maior)</SelectItem>
                <SelectItem value="amount_asc">Valor (menor)</SelectItem>
                <SelectItem value="buyer_name">Nome do comprador</SelectItem>
              </SelectContent>
            </Select>

            {/* Contador de resultados */}
            <div className="flex items-center text-gray-400">
              <span className="text-sm">
                {filteredSales.length} de {sales.length} vendas
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Vendas */}
      <Card className="bg-[#1E293B] border-[#2A3749]">
        <CardHeader>
          <CardTitle className="text-white">Histórico de Vendas</CardTitle>
          <p className="text-sm text-gray-400 mt-2">
            💡 <strong>Valores das mentorias:</strong> Mostram o valor efetivamente pago pelo cliente no momento da compra
          </p>
        </CardHeader>
        <CardContent>
          {filteredSales.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-400">Nenhuma venda encontrada com os filtros aplicados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#2A3749]">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Data</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Tipo</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Produto/Serviço</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Comprador</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Valor Bruto</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Comissão</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Valor Líquido</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.map((sale) => (
                    <tr key={sale.id} className="border-b border-[#2A3749]/50 hover:bg-[#2A3749]/20">
                      <td className="py-3 px-4 text-white">
                        {formatDate(sale.date)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {sale.type === 'mentorship' ? (
                            <>
                              <Users className="h-4 w-4 text-blue-400" />
                              <span className="text-blue-400">Mentoria</span>
                            </>
                          ) : (
                            <>
                              <FileText className="h-4 w-4 text-green-400" />
                              <span className="text-green-400">Conteúdo</span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-white max-w-48 truncate" title={sale.title}>
                        {sale.title}
                      </td>
                      <td className="py-3 px-4 text-white font-medium">
                        {sale.buyer_name}
                      </td>
                      <td className="py-3 px-4 text-white font-medium">
                        {formatCurrency(sale.gross_amount_cents)}
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <div className="text-orange-400 font-medium">
                            {formatCurrency(sale.commission_amount_cents)}
                          </div>
                          <div className="text-gray-400 text-sm">
                            {formatPercentage(sale.commission_rate)}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-green-400 font-medium">
                        {formatCurrency(sale.net_amount_cents)}
                      </td>
                      <td className="py-3 px-4">
                        <Badge 
                          variant={
                            sale.status === 'completed' || sale.status === 'released' ? 'default' : 
                            sale.status === 'pending_release' ? 'secondary' : 'outline'
                          }
                          className={
                            sale.status === 'completed' || sale.status === 'released' ? 'bg-green-600 text-white' :
                            sale.status === 'pending_release' ? 'bg-yellow-600 text-white' : ''
                          }
                        >
                          {sale.status === 'completed' ? 'Concluído' :
                           sale.status === 'released' ? 'Liberado' :
                           sale.status === 'pending_release' ? 'Aguardando Liberação' : 
                           sale.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DetalhesVendas; 